import { Bar } from '@ant-design/plots'
import { Select } from 'components/Select'
import { TGroup, TOption } from 'components/Select/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getAggregatedSurveyResults } from './api'
import {
  IRFPHospitalUser,
  IRFPVendorCCPayload,
  RFPVotingResultsData,
  TRankedVendorFullData
} from './types'
import './styles.scss'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { setLoading } from 'redux/store/common/slice'
import { getIsLoading } from 'redux/store/common/getters'
import { getUser } from 'redux/store/user/getters'
import { getUniqueValues } from './CreateSurvey/helpers'
import { BarConfig } from '@ant-design/plots/es/components/bar'
import IconNameItem from '../../../components/IconNameItem/IconNameItem'
import { useRFPSurveyContext } from '../RFPDetails/Providers/RFPSurveyContextProvider'
import { useRFPDetailsPopupContext } from '../RFPDetails/Providers/RFPDetailsPopupProvider'
import { useRFPCreateSurveyContext } from './CreateSurvey/Providers/RFPCreateSurveyContextProvider'
import { SurveyResultsBody } from './SurveyResultsBody'
import { Link } from 'components/Link'
import { routes } from 'router'
import { ROLES } from 'features/Permission'
import { useRFPDetailsContext } from 'features/RFP/RFPDetails/Providers/RFPDetailsContextProvider'
import { getSurveyProxySubmittedByLabel } from './getSurveyProxySubmittedByLabel'
import { useRFPVotingContext } from '../RFPDetails/Providers/RFPVotingContextProvider'
import { formatOptionIdValue } from '../../../helper/optionsFormatters'

export const SurveyResults = ({ rfpID }: { rfpID: string }) => {
  const { state: rfpDetailsState } = useRFPDetailsContext()
  const rfpCreatorUuid = rfpDetailsState.data?.creator?.uuid

  const { state, actions } = useRFPSurveyContext()
  const { allUserOptions, contractCategories, surveyResponse } = state
  const { getRfpVendorCCListAsync } = actions
  const { submitSurveyPopup } = useRFPDetailsPopupContext()
  const { actions: rfpCreateSurveyActions } = useRFPCreateSurveyContext()
  const rfpVotingContext = useRFPVotingContext()
  const { isVotingInProgress } = rfpVotingContext.state

  const [stakeholders, setStakeholders] = useState<IRFPHospitalUser[]>([])
  const [selectedStakeholder, setSelectedStakeHolder] = useState<string>('all')
  const [surveyData, setSurveyData] = useState<RFPVotingResultsData['vote']>([])

  const dispatch = useDispatch()

  const isLoading = useSelector(getIsLoading)
  const user = useSelector(getUser)
  const isClinicalCoordinatorRole = user?.role === ROLES.CLINICAL_COORDINATOR
  const isCurrentUserFromCommunity = user?.role?.startsWith('community_')

  useEffect(() => {
    dispatch(setLoading(true))
    getAggregatedSurveyResults(rfpID)
      .then((res) => {
        if (res?.data.vote) {
          const stakeholder = res.data.vote.flatMap((vote) =>
            vote.ranked_vendors.map((vendorData) => vendorData.user)
          )
          const uniqueStakeholderIds = getUniqueValues(
            stakeholder.map((data) => data.uuid)
          )
          setStakeholders(
            uniqueStakeholderIds
              .map((id) => stakeholder.find((user) => user.uuid === id))
              .filter((item) => !!item) as IRFPHospitalUser[]
          )
          setSurveyData(res.data.vote)
        } else {
          setStakeholders([])
          setSurveyData([])
        }
      })
      .finally(() => dispatch(setLoading(false)))
    // Refetch when a vote is submitted/updated in context (e.g. proxy vote) so Submitted / Not responded lists stay in sync
  }, [dispatch, rfpID, surveyResponse])

  const filteredSurveyData = useMemo(() => {
    if (selectedStakeholder && selectedStakeholder !== 'all') {
      return surveyData
        .map((item) => ({
          ...item,
          ranked_vendors: item.ranked_vendors.filter(
            (vendor) => vendor.user.uuid === selectedStakeholder
          )
        }))
        .filter((item) => !!item.ranked_vendors.length)
    }
    return surveyData
  }, [selectedStakeholder, surveyData])

  const surveyWithMergedAndSortedGrades = useMemo(
    () =>
      filteredSurveyData.map((survey) => ({
        ...survey,
        ranked_vendors: survey.ranked_vendors
          .reduce((acc, gradeData) => {
            const prevItem = acc.find(
              (item) => item.vendor.uuid === gradeData.vendor.uuid
            )
            if (prevItem) {
              return acc.map((item) => ({
                ...item,
                grade:
                  item.vendor.uuid === gradeData.vendor.uuid
                    ? item.grade + gradeData.grade
                    : item.grade
              }))
            }
            return [...acc, gradeData]
          }, [] as TRankedVendorFullData['ranked_vendors'])
          .sort((a, b) => b.grade - a.grade)
      })),
    [filteredSurveyData]
  )

  const voteRowByCategoryUuid = useMemo(() => {
    const map = new Map<string, TRankedVendorFullData>()
    surveyWithMergedAndSortedGrades.forEach((row) => {
      if (row.category?.uuid) {
        map.set(row.category.uuid, row)
      }
    })
    return map
  }, [surveyWithMergedAndSortedGrades])

  const submittedStakeholderIds = useMemo(() => {
    return new Set(stakeholders.map((stakeholder) => stakeholder.uuid))
  }, [stakeholders])

  const isNotRespondedSelected =
    !!selectedStakeholder &&
    selectedStakeholder !== 'all' &&
    !submittedStakeholderIds.has(selectedStakeholder)

  const shouldUseReadonlyEmptyState = useMemo(() => {
    if (!selectedStakeholder || selectedStakeholder === 'all') {
      return false
    }

    const selectedUserRole = allUserOptions.find(
      (stakeholder) => stakeholder.id === selectedStakeholder
    )?.role

    return (
      isCurrentUserFromCommunity &&
      selectedUserRole === ROLES.COMMONS_STAKEHOLDER
    )
  }, [allUserOptions, isCurrentUserFromCommunity, selectedStakeholder])

  const fetchedCategories = useMemo(() => {
    if (!selectedStakeholder) return []
    return contractCategories[selectedStakeholder] ?? []
  }, [contractCategories, selectedStakeholder])

  const categoriesToShow = useMemo(() => {
    const map = new Map<string, IRFPVendorCCPayload>()
    fetchedCategories.forEach((c) => map.set(c.id, c))
    return Array.from(map.values())
  }, [fetchedCategories])

  useEffect(() => {
    if (!selectedStakeholder) return
    if (contractCategories[selectedStakeholder]?.length) return

    getRfpVendorCCListAsync(selectedStakeholder, 'user')
  }, [getRfpVendorCCListAsync, selectedStakeholder, contractCategories])

  useEffect(() => {
    if (isClinicalCoordinatorRole) {
      setSelectedStakeHolder(
        stakeholders?.[0]?.uuid ?? allUserOptions?.[0]?.id ?? ''
      )
    }
  }, [allUserOptions, isClinicalCoordinatorRole, stakeholders])

  const returnSelectOptions = (arr: IRFPHospitalUser[]): TGroup[] => {
    const submittedUserIds = new Set(arr.map((item) => item.uuid))

    const submittedOptions: TOption[] = arr.map((item) => ({
      label: `${item.first_name} ${item.last_name}`,
      value: item.uuid
    }))

    const notRespondedOptions: TOption[] =
      allUserOptions
        ?.filter((user) => !submittedUserIds.has(user.id))
        ?.filter((user) => !!user.is_voting_panel_member)
        .map(formatOptionIdValue) ?? []

    const options: TGroup[] = []

    if (!isClinicalCoordinatorRole) {
      options.push({
        label: ``,
        options: [{ label: `All responses (${arr.length})`, value: 'all' }]
      })
    }

    options.push({ label: 'Submitted', options: submittedOptions })

    options.push({ label: 'Not responded', options: notRespondedOptions })

    return options
  }

  const returnSurveyData = (arr: TRankedVendorFullData) => {
    if (arr?.ranked_vendors.length) {
      return arr.ranked_vendors.map((item) => ({
        vendor: item?.vendor?.name,
        grade: item?.grade
      }))
    }
    return []
  }

  const getConfig = useCallback(
    (data: TRankedVendorFullData): BarConfig => ({
      data: returnSurveyData(data),
      yField: 'vendor',
      xField: 'grade',
      tooltip: false as const,
      yAxis: {
        grid: {
          line: {
            style: {
              stroke: '#E9EEF7',
              lineWidth: 1,
              shadowColor: '#D9D9D9',
              shadowOffsetY: -51,
              cursor: 'pointer'
            }
          }
        },
        tickLine: {
          length: 0
        },
        subTickLine: {
          count: 1,
          length: 100,
          style: {
            stroke: '#E9EEF7',
            lineWidth: 1,
            shadowColor: '#D9D9D9',
            shadowOffsetY: -3,
            cursor: 'pointer'
          }
        },
        label: {
          style: {
            fill: '#363C43',
            fontSize: 14
          }
        }
      },
      label: {
        position: 'left',
        offset: 3,
        style: {
          fill: 'white',
          fontSize: 12
        }
      },
      annotations: [
        {
          type: 'line',
          start: ['-100%', '100%'],
          end: ['100%', '100%'],
          style: {
            stroke: '#D9D9D9',
            lineWidth: 1
          }
        },
        {
          type: 'line',
          start: ['-100%', '0.1%'],
          end: ['100%', '0.1%'],
          style: {
            stroke: '#D9D9D9',
            lineWidth: 1
          }
        }
      ],
      color: '#2767A9',
      minBarWidth: 20,
      maxBarWidth: 20
    }),
    []
  )

  const showRespondedStakeholderMergedView = useMemo(
    () =>
      selectedStakeholder !== 'all' &&
      submittedStakeholderIds.has(selectedStakeholder) &&
      surveyData.length > 0,
    [selectedStakeholder, submittedStakeholderIds, surveyData.length]
  )

  const onVoteForCategory = useCallback(
    (categoryId: string) => {
      rfpCreateSurveyActions.setSingleCategoryUuid(categoryId)
      submitSurveyPopup.actions.open({
        stakeholderId:
          selectedStakeholder !== 'all' ? selectedStakeholder : undefined,
        categoryId: selectedStakeholder === 'all' ? categoryId : undefined
      })
    },
    [rfpCreateSurveyActions, selectedStakeholder, submitSurveyPopup.actions]
  )

  const getChartData = useCallback(
    (data: TRankedVendorFullData) => {
      return (
        <>
          <div className="flex space-between">
            {!!data.category && (
              <div className="contract-category-title">
                <Typography.Label className="mb-4 contract-category-label">
                  Contract Category
                </Typography.Label>
                <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.BOLD}>
                  {data.category.name}
                </Typography.Body1>
              </div>
            )}
            {selectedStakeholder !== 'all' &&
              data.ranked_vendors?.[0]?.proxy_voter && (
                <div className="row align-center gap-8 submitted-by submitted-by-border ">
                  <Typography.Body2>
                    {getSurveyProxySubmittedByLabel(
                      data.ranked_vendors?.[0]?.proxy_voter,
                      rfpCreatorUuid
                    )}
                  </Typography.Body2>
                  <Link
                    className="row gap-4 align-center"
                    to={`${routes.createUserProfile}/${data.ranked_vendors?.[0]?.proxy_voter.uuid}?fromCommunity=true`}
                  >
                    <IconNameItem
                      firstName={
                        data.ranked_vendors?.[0]?.proxy_voter?.first_name
                      }
                      lastName={
                        data.ranked_vendors?.[0]?.proxy_voter?.last_name
                      }
                      src={null}
                      roleName={''}
                    />
                  </Link>
                </div>
              )}
          </div>
          <div className="survey-results__chart-container">
            <div className="flex space-between">
              <Typography.Label weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                Most preferred
              </Typography.Label>
              <Typography.Label weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                Number of points
              </Typography.Label>
            </div>
            <div
              className="survey-results__bar-container"
              style={{
                height: `calc(${
                  getUniqueValues(
                    data.ranked_vendors.map((item) => item.vendor.uuid)
                  ).length
                } * 44px + 28px)`
              }}
            >
              <Bar className="survey-results__bar" {...getConfig(data)} />
            </div>
            <div className="chart-bottom-label">
              <Typography.Label weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                Least preferred
              </Typography.Label>
            </div>
          </div>
        </>
      )
    },
    [getConfig, selectedStakeholder, rfpCreatorUuid]
  )

  return (
    <>
      {isVotingInProgress && (
        <div className="user-select-container mt-24">
          <Select
            key={selectedStakeholder}
            propsItem={{
              name: 'select',
              label: 'User',
              rules: [{ required: true }]
            }}
            className="survey-results__select"
            options={returnSelectOptions(stakeholders)}
            propsSelect={{
              defaultValue: !!stakeholders && selectedStakeholder,
              loading: !stakeholders?.length,
              onChange: setSelectedStakeHolder,
              value: selectedStakeholder,
              placeholder: isClinicalCoordinatorRole
                ? 'Select user'
                : `All responses (${stakeholders?.length})`
            }}
          />
        </div>
      )}
      {!isLoading && isVotingInProgress && (
        <SurveyResultsBody
          isNotRespondedSelected={isNotRespondedSelected}
          shouldUseReadonlyEmptyState={shouldUseReadonlyEmptyState}
          showRespondedStakeholderMergedView={
            showRespondedStakeholderMergedView
          }
          hasSurveyData={surveyData.length > 0}
          categoriesToShow={categoriesToShow}
          voteRowByCategoryUuid={voteRowByCategoryUuid}
          surveyWithMergedAndSortedGrades={surveyWithMergedAndSortedGrades}
          onVoteForCategory={onVoteForCategory}
          renderCategoryChart={getChartData}
        />
      )}
    </>
  )
}
